if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.initialized = false;
        this.mql = window.matchMedia('(min-width: 750px)');
      }

      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mediaList: this.querySelector('[id^="Slider-Gallery"]'),
        };

        if (!this.elements.mediaList) return;

        this.setupThumbnailClicks();
        this.setupSlider();
        this.setInitialMedia();
      }

      /*
       * =====================================================
       * THUMBNAILS
       * =====================================================
       */

      setupThumbnailClicks() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('[data-target]');

          if (!thumbnail) return;

          const mediaId = thumbnail.dataset.target;

          if (!mediaId) return;

          event.preventDefault();
          event.stopPropagation();

          this.setActiveMedia(mediaId);
        });
      }

      /*
       * =====================================================
       * SLIDER
       * =====================================================
       */

      setupSlider() {
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce((event) => {
            if (!event.detail?.currentElement) return;

            const media = event.detail.currentElement;

            if (!media.dataset.mediaId) return;

            this.setActiveMedia(
              media.dataset.mediaId,
              false,
              true
            );
          }, 150)
        );
      }

      /*
       * =====================================================
       * INITIAL MEDIA
       * =====================================================
       */

      setInitialMedia() {
        const mediaItems =
          this.getMediaItems();

        if (!mediaItems.length) return;

        let activeMedia =
          mediaItems.find((media) =>
            media.classList.contains('is-active')
          );

        if (!activeMedia) {
          activeMedia = mediaItems[0];
        }

        this.activateMediaElement(activeMedia);

        this.updateThumbnail(
          activeMedia.dataset.mediaId,
          false
        );

        if (this.mql.matches) {
          this.showDesktopMedia(activeMedia);
        }
      }

      /*
       * =====================================================
       * GET MEDIA ITEMS
       * =====================================================
       */

      getMediaItems() {
        return Array.from(
          this.elements.mediaList.querySelectorAll(
            '[data-media-id]'
          )
        );
      }

      /*
       * =====================================================
       * FIND MEDIA
       * =====================================================
       */

      getMediaById(mediaId) {
        return this.getMediaItems().find(
          (media) =>
            media.dataset.mediaId === mediaId
        );
      }

      /*
       * =====================================================
       * SET ACTIVE MEDIA
       * =====================================================
       */

      setActiveMedia(
        mediaId,
        prepend = false,
        fromSlider = false
      ) {
        const activeMedia =
          this.getMediaById(mediaId);

        if (!activeMedia) {
          console.warn(
            'Media Gallery: no se encontró la media:',
            mediaId
          );
          return;
        }

        /*
         * Activar media
         */

        this.activateMediaElement(
          activeMedia
        );

        /*
         * Desktop
         */

        if (this.mql.matches) {
          this.showDesktopMedia(
            activeMedia
          );
        }

        /*
         * Mobile
         */

        else if (!fromSlider) {
          activeMedia.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          });
        }

        /*
         * Thumbnail activo
         */

        this.updateThumbnail(
          mediaId,
          !fromSlider
        );

        /*
         * Reproducir vídeo / modelo
         */

        this.playActiveMedia(
          activeMedia
        );

        /*
         * Header
         */

        this.preventStickyHeader();

        /*
         * Accesibilidad
         */

        if (!fromSlider) {
          const thumbnail =
            this.elements.thumbnails?.querySelector(
              `[data-target="${mediaId}"]`
            );

          if (thumbnail) {
            this.announceLiveRegion(
              activeMedia,
              thumbnail.dataset.mediaPosition
            );
          }
        }
      }

      /*
       * =====================================================
       * ACTIVATE MEDIA ELEMENT
       * =====================================================
       */

      activateMediaElement(activeMedia) {
        this.getMediaItems().forEach(
          (media) => {
            media.classList.remove(
              'is-active'
            );
          }
        );

        activeMedia.classList.add(
          'is-active'
        );
      }

      /*
       * =====================================================
       * DESKTOP
       * =====================================================
       */

      showDesktopMedia(activeMedia) {
        this.getMediaItems().forEach(
          (media) => {
            if (media === activeMedia) {
              media.classList.add(
                'is-active'
              );
            } else {
              media.classList.remove(
                'is-active'
              );
            }
          }
        );
      }

      /*
       * =====================================================
       * UPDATE THUMBNAIL
       * =====================================================
       */

      updateThumbnail(
        mediaId,
        scroll = true
      ) {
        if (!this.elements.thumbnails) {
          return;
        }

        const thumbnails =
          Array.from(
            this.elements.thumbnails.querySelectorAll(
              '[data-target]'
            )
          );

        thumbnails.forEach(
          (thumbnail) => {
            const button =
              thumbnail.querySelector(
                'button'
              );

            if (!button) return;

            button.removeAttribute(
              'aria-current'
            );
          }
        );

        const activeThumbnail =
          thumbnails.find(
            (thumbnail) =>
              thumbnail.dataset.target ===
              mediaId
          );

        if (!activeThumbnail) return;

        const button =
          activeThumbnail.querySelector(
            'button'
          );

        if (button) {
          button.setAttribute(
            'aria-current',
            'true'
          );
        }

        /*
         * Llevar thumbnail seleccionado
         * al centro de la fila.
         */

        if (scroll) {
          activeThumbnail.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }

      /*
       * =====================================================
       * PLAY MEDIA
       * =====================================================
       */

      playActiveMedia(activeItem) {
        if (
          typeof window.pauseAllMedia ===
          'function'
        ) {
          window.pauseAllMedia();
        }

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (
          deferredMedia &&
          typeof deferredMedia.loadContent ===
            'function'
        ) {
          deferredMedia.loadContent(false);
        }
      }

      /*
       * =====================================================
       * ACCESSIBILITY
       * =====================================================
       */

      announceLiveRegion(
        activeItem,
        position
      ) {
        if (!this.elements.liveRegion) {
          return;
        }

        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            'false'
          );

          if (
            window.accessibilityStrings?.imageAvailable
          ) {
            this.elements.liveRegion.innerHTML =
              window.accessibilityStrings.imageAvailable.replace(
                '[index]',
                position
              );
          }

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              'true'
            );
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.addEventListener(
            'load',
            announce,
            { once: true }
          );
        }
      }

      /*
       * =====================================================
       * STICKY HEADER
       * =====================================================
       */

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }
    }
  );
}